import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading, Toggle } from '../components/ui';
import { useApi, useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface BeatsContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
}

interface Beat {
  id: number;
  title: string;
  filename: string;
  audio_url?: string;
  bpm?: number;
  key?: string;
  tags_en?: string;
  tags_ro?: string;
  for_sale: boolean;
  price?: number;
  sort_order: number;
}

const BeatsPage: React.FC = () => {
  const { data, loading, fetch, update } = useApi<BeatsContent>('/api/content/beats-page');
  const beats = useCrud<Beat>('/api/beats');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<BeatsContent>({
    title_en: '', title_ro: '',
    subtitle_en: '', subtitle_ro: '',
  });
  const [saving, setSaving] = useState(false);
  const [editingBeat, setEditingBeat] = useState<Beat | null>(null);

  useEffect(() => { fetch(); beats.fetchAll(); }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        title_en: data.title_en || '',
        title_ro: data.title_ro || '',
        subtitle_en: data.subtitle_en || '',
        subtitle_ro: data.subtitle_ro || '',
      });
    }
  }, [data]);

  const handleChange = (field: keyof BeatsContent) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('Beats page header saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  const handleBeatSave = async (beat: Partial<Beat>) => {
    if (editingBeat?.id) {
      const result = await beats.update(editingBeat.id, beat);
      if (result) {
        showToast('Beat updated!', 'success');
        setEditingBeat(null);
      }
    } else {
      const result = await beats.create(beat);
      if (result) {
        showToast('Beat created!', 'success');
        setEditingBeat(null);
      }
    }
  };

  const handleBeatDelete = async (id: number) => {
    if (window.confirm('Delete this beat?')) {
      const result = await beats.remove(id);
      if (result) showToast('Beat deleted', 'success');
    }
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Beats Gallery"
        subtitle="Manage the beats gallery page and individual beats"
        actions={<Button onClick={handleSave} loading={saving}>Save Header</Button>}
      />

      <div className="admin-content">
        <Card title="Page Header">
          <LanguageTabs currentLang={lang} onChange={setLang} />
          <div className="admin-form">
            {lang === 'en' ? (
              <>
                <Input label="Title (EN)" value={form.title_en} onChange={handleChange('title_en')} placeholder="Custom Beats" />
                <Input label="Subtitle (EN)" value={form.subtitle_en} onChange={handleChange('subtitle_en')} placeholder="Explore my latest instrumentals" />
              </>
            ) : (
              <>
                <Input label="Title (RO)" value={form.title_ro} onChange={handleChange('title_ro')} placeholder="Beaturi personalizate" />
                <Input label="Subtitle (RO)" value={form.subtitle_ro} onChange={handleChange('subtitle_ro')} placeholder="Explorează cele mai recente instrumentale" />
              </>
            )}
          </div>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card
            title="Beats"
            actions={
              <Button size="sm" onClick={() => setEditingBeat({ id: 0, title: '', filename: '', for_sale: false, sort_order: beats.items.length })}>
                + Add Beat
              </Button>
            }
          >
            <div className="admin-list-editor">
              {beats.items.map(beat => (
                <div key={beat.id} className="admin-list-item">
                  <span style={{ fontSize: '1.2rem' }}>🎵</span>
                  <div className="admin-list-item-content">
                    <div>
                      <strong>{beat.title}</strong>
                      <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                        {beat.bpm && `${beat.bpm} BPM`} {beat.key && `• ${beat.key}`} {beat.for_sale && `• $${beat.price}`}
                      </div>
                    </div>
                  </div>
                  <div className="admin-list-item-actions">
                    <Button size="sm" variant="secondary" onClick={() => setEditingBeat(beat)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => handleBeatDelete(beat.id)}>Delete</Button>
                  </div>
                </div>
              ))}
              {beats.items.length === 0 && (
                <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 20 }}>
                  No beats yet. Add your first beat above.
                </p>
              )}
            </div>
          </Card>
        </div>

        {editingBeat && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingBeat(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingBeat.id ? 'Edit Beat' : 'Add Beat'}</h3>
              <BeatForm
                beat={editingBeat}
                onSave={handleBeatSave}
                onCancel={() => setEditingBeat(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const BeatForm: React.FC<{
  beat: Beat;
  onSave: (b: Partial<Beat>) => void;
  onCancel: () => void;
}> = ({ beat, onSave, onCancel }) => {
  const [form, setForm] = useState(beat);

  return (
    <div className="admin-form">
      <Input label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
      <Input label="Filename" value={form.filename || ''} onChange={e => setForm(f => ({ ...f, filename: e.target.value }))} placeholder="1.mp3" hint="Local file in /beats folder" />
      <Input label="Audio URL (optional)" value={form.audio_url || ''} onChange={e => setForm(f => ({ ...f, audio_url: e.target.value }))} placeholder="https://..." hint="External audio URL (overrides filename)" />
      <div className="admin-form-row">
        <Input label="BPM" type="number" value={form.bpm || ''} onChange={e => setForm(f => ({ ...f, bpm: parseInt(e.target.value) || undefined }))} />
        <Input label="Key" value={form.key || ''} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} placeholder="C Minor" />
      </div>
      <Input label="Tags (EN)" value={form.tags_en || ''} onChange={e => setForm(f => ({ ...f, tags_en: e.target.value }))} placeholder="trap, dark, melodic" />
      <Input label="Tags (RO)" value={form.tags_ro || ''} onChange={e => setForm(f => ({ ...f, tags_ro: e.target.value }))} placeholder="trap, întunecat, melodic" />
      <Toggle
        checked={form.for_sale}
        onChange={checked => setForm(f => ({ ...f, for_sale: checked }))}
        label="Available for purchase"
      />
      {form.for_sale && (
        <Input label="Price ($)" type="number" value={form.price || ''} onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || undefined }))} />
      )}
      <Input label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default BeatsPage;
